import { type ReactNode, useEffect, useMemo, useState } from "react";

import type { ChildTarget } from "@sytofood/shared";

import { useModalScrollLock } from "../hooks/use-modal-scroll-lock";
import {
  type DailySummaryView,
  type IngredientView,
  type LoggedMeal,
  type TargetsView,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { todayIso } from "../lib/day";
import { useLang } from "../lib/i18n";
import { childrenCopy } from "../lib/i18n/children";
import { type MealsCopy, mealsCopy } from "../lib/i18n/meals";
import { useProfile } from "../lib/profile-context";
import { EditMealModal } from "./edit-meal-modal";
import { FavoritesStrip } from "./favorites-strip";
import { ScreenShell } from "./screen-shell";
import { IconStar } from "./ui/icon";
import { InviteChip } from "./ui/invite-chip";
import { Eyebrow, MonoMeta, PrimaryButton } from "./ui/primitives";
import { SytoLogo } from "./ui/syto-logo";

type Props = {
  householdId: string;
  targets: TargetsView;
  // Optional element rendered in the right side of the top bar, below the
  // date. Used by HomeMaybeADHD to inline the SPOKÓJ pill instead of an
  // absolute overlay that overlapped the date.
  headerRight?: ReactNode;
  // Optional element rendered next to the logo on the left side of the top
  // bar. Used to inline the profile switcher chip (ADR-0018) without
  // colliding with the date / ADHD pill on the right.
  headerLeft?: ReactNode;
};


function shiftDay(day: string, delta: number): string {
  const ts = Date.parse(`${day}T00:00:00.000Z`) + delta * 24 * 60 * 60 * 1000;
  const d = new Date(ts);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
}

const DASH_INDICES = Array.from({ length: 30 }, (_, i) => i);

function formatHeaderDate(day: string, t: MealsCopy): string {
  const isoToday = todayIso();
  const d = new Date(`${day}T00:00:00.000Z`);
  const wk = t.weekdayShort[d.getUTCDay()];
  const dm = `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  if (day === isoToday) return `${wk} · ${dm} · ${t.todayTag}`;
  if (day === shiftDay(isoToday, -1)) return `${wk} · ${dm} · ${t.yesterdayTag}`;
  return `${wk} · ${dm}`;
}

function totalsFromMeals(meals: DailySummaryView["meals"]) {
  return meals.reduce(
    (acc, m) => ({
      kcal: acc.kcal + m.kcal,
      proteinG: acc.proteinG + (m.proteinG ?? 0),
      fatG: acc.fatG + (m.fatG ?? 0),
      carbsG: acc.carbsG + (m.carbsG ?? 0),
    }),
    { kcal: 0, proteinG: 0, fatG: 0, carbsG: 0 },
  );
}

function greetingTone(
  remainingKcal: number,
  kcalTarget: number,
  mealsLogged: number,
  t: MealsCopy,
): string {
  if (kcalTarget === 0) return t.toneNoTarget;
  if (mealsLogged === 0) return t.toneEmptyDay;
  const pct = remainingKcal / kcalTarget;
  if (pct > 0.6) return t.toneBigWindow;
  if (pct > 0.3) return t.toneMidDay;
  if (pct > 0.05) return t.toneSmallSpace;
  if (pct >= 0) return t.toneGoalClosed;
  return t.toneSlightlyOver;
}

export function DailyTrackerScreen({ householdId, targets, headerRight, headerLeft }: Props) {
  const { state } = useAuth();
  const { lang } = useLang();
  const t = mealsCopy[lang];
  const tChildren = childrenCopy[lang];
  // ADR-0018: when active.kind === "child" we swap the data source for both
  // the kcal hero (child's Henry-derived target) and the meal list (child's
  // own meals, scoped by child_id). When active.kind === "self" the screen
  // behaves exactly like before for users without children.
  const { active, children: childProfiles } = useProfile();
  // Resolved Child object for the active child profile (null in self view).
  // Kept as a derived value so a parallel rename via the Children screen
  // refresh propagates here without an extra fetch.
  const activeChild =
    active.kind === "child"
      ? (childProfiles.find((c) => c.id === active.childId) ?? null)
      : null;
  // Reference so TS strict noUnusedLocals doesn't flag — the value lives on
  // for an upcoming "Dziennik: <name>" subtitle (DESIGN.md TBD), and reading
  // it here also guarantees the profile list is loaded before render.
  void activeChild;
  const [day, setDay] = useState<string>(() => todayIso());
  const [summary, setSummary] = useState<DailySummaryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [childTarget, setChildTarget] = useState<ChildTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingMeal, setEditingMeal] = useState<LoggedMeal | null>(null);
  // Nested-meal expansion (ADR-0010): which dish is expanded + lazy-loaded
  // ingredient rows keyed by meal id.
  const [expandedMealId, setExpandedMealId] = useState<string | null>(null);
  const [ingredientsByMeal, setIngredientsByMeal] = useState<Record<string, IngredientView[]>>(
    {},
  );
  const [favIngMealId, setFavIngMealId] = useState<string | null>(null);
  const [favedMealIds, setFavedMealIds] = useState<Set<string>>(new Set());
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ code: string; invites: number } | null>(null);

  const user = state.status === "signed-in" ? state.user : null;
  const showVerifyBanner = user !== null && !user.emailVerified;
  const isToday = day === todayIso();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const opts = active.kind === "child" ? { childId: active.childId } : undefined;
    api
      .getDailySummary(householdId, day, opts)
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t.loadError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, day, active, t.loadError]);

  // Pull the child's Henry / override target only when the active profile is
  // a child. Cleared on profile change so a stale target never leaks into
  // the kcal hero of a different child.
  useEffect(() => {
    if (active.kind !== "child") {
      setChildTarget(null);
      return;
    }
    let cancelled = false;
    api
      .getChildTarget(householdId, active.childId)
      .then((res) => {
        if (!cancelled) setChildTarget(res);
      })
      .catch(() => {
        if (!cancelled) setChildTarget(null);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, active]);

  useEffect(() => {
    let cancelled = false;
    api
      .getMyInvite()
      .then((res) => {
        if (!cancelled) setInvite(res);
      })
      .catch(() => {
        // Non-fatal: chip just won't render. Don't break the screen on this.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleResendVerification() {
    setResending(true);
    setResendMsg(null);
    try {
      await api.resendVerification();
      setResendMsg(t.resendDone(user?.email ?? ""));
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : t.resendError);
    } finally {
      setResending(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const mealId = pendingDelete.id;
    setDeletingId(mealId);
    setError(null);
    try {
      await api.deleteMeal(householdId, mealId);
      setSummary((prev) => {
        if (!prev) return prev;
        const meals = prev.meals.filter((m) => m.id !== mealId);
        return { ...prev, meals, totals: totalsFromMeals(meals) };
      });
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.deleteError);
    } finally {
      setDeletingId(null);
    }
  }

  function applyEdit(updated: LoggedMeal) {
    setSummary((prev) => {
      if (!prev) return prev;
      const meals = prev.meals.map((m) => (m.id === updated.id ? { ...m, ...updated } : m));
      return { ...prev, meals, totals: totalsFromMeals(meals) };
    });
    setEditingMeal(null);
  }

  // Toggles the ingredient breakdown of a dish; lazy-loads on first open.
  async function toggleExpand(meal: LoggedMeal) {
    if (expandedMealId === meal.id) {
      setExpandedMealId(null);
      return;
    }
    setExpandedMealId(meal.id);
    if (!ingredientsByMeal[meal.id]) {
      try {
        const ings = await api.getMealIngredients(householdId, meal.id);
        setIngredientsByMeal((prev) => ({ ...prev, [meal.id]: ings }));
      } catch {
        // non-fatal — the row just won't expand
      }
    }
  }

  async function saveAsFavorite(meal: LoggedMeal) {
    setFavIngMealId(meal.id);
    setError(null);
    try {
      await api.createFavorite(householdId, {
        name: meal.name,
        kcal: meal.kcal,
        proteinG: meal.proteinG,
        fatG: meal.fatG,
        carbsG: meal.carbsG,
        portionGrams: meal.portionGrams,
      });
      setFavedMealIds((prev) => new Set(prev).add(meal.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.favoriteError);
    } finally {
      setFavIngMealId(null);
    }
  }

  const greetingName = useMemo(() => {
    if (state.status === "signed-in" && state.user.name) return state.user.name;
    return null;
  }, [state]);

  const kcalCurrent = summary?.totals.kcal ?? 0;
  // When the active profile is a child, swap the kcal + macro targets to
  // the child's Henry / override values. childTarget may be null while the
  // fetch is in flight; the hero degrades to "?" the same way as a user
  // without targets set.
  const effectiveKcalTarget =
    active.kind === "child"
      ? (childTarget?.kcalTarget ?? 0)
      : (targets.kcalTarget ?? 0);
  const effectiveProteinTarget =
    active.kind === "child"
      ? (childTarget?.proteinTargetG ?? 0)
      : (targets.proteinTargetG ?? 0);
  const effectiveFatTarget =
    active.kind === "child"
      ? (childTarget?.fatTargetG ?? 0)
      : (targets.fatTargetG ?? 0);
  const effectiveCarbsTarget =
    active.kind === "child"
      ? (childTarget?.carbsTargetG ?? 0)
      : (targets.carbsTargetG ?? 0);
  const kcalTarget = effectiveKcalTarget;
  const kcalRemaining = Math.max(0, kcalTarget - kcalCurrent);
  const mealsLogged = summary?.meals.length ?? 0;
  const pctDone = kcalTarget > 0 ? Math.min(100, Math.round((kcalCurrent / kcalTarget) * 100)) : 0;
  const filledBars = kcalTarget > 0 ? Math.round((kcalCurrent / kcalTarget) * 30) : 0;

  const macros = [
    {
      l: t.macroProtein,
      v: summary?.totals.proteinG ?? 0,
      t: effectiveProteinTarget,
    },
    {
      l: t.macroFat,
      v: summary?.totals.fatG ?? 0,
      t: effectiveFatTarget,
    },
    {
      l: t.macroCarbs,
      v: summary?.totals.carbsG ?? 0,
      t: effectiveCarbsTarget,
    },
  ];

  // Soft banner shown when a child's Henry inputs are missing. Adult mode
  // never shows this; we have a different (kcalTarget=0) path for that.
  const showChildIncompleteBanner =
    active.kind === "child" && childTarget !== null && childTarget.source === "incomplete";

  return (
    <ScreenShell hideHeader withBottomNav>
      <header
        className="flex items-start justify-between gap-3"
        style={{ marginBottom: 12 }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <SytoLogo size={20} />
          {headerLeft}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <MonoMeta>{formatHeaderDate(day, t)}</MonoMeta>
          {headerRight}
        </div>
      </header>

      {invite ? (
        <div style={{ marginBottom: 12 }}>
          <InviteChip code={invite.code} invites={invite.invites} />
        </div>
      ) : null}

      {showChildIncompleteBanner ? (
        <div
          className="rounded-lg p-3"
          style={{
            background: "var(--surface-2)",
            color: "var(--ink-2)",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {tChildren.chTargetBanner}
        </div>
      ) : null}

      {showVerifyBanner ? (
        <div
          className="rounded-lg p-3 flex flex-col gap-1"
          style={{ background: "var(--warn-bg)", color: "var(--warn-fg)", marginBottom: 12 }}
        >
          <p className="text-[13px]">
            {t.verifyBanner} <strong>{user?.email}</strong>.
          </p>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resending}
            className="text-[11px] underline self-start"
          >
            {resending ? t.resending : t.resendAgain}
          </button>
          {resendMsg ? <p className="text-[11px]">{resendMsg}</p> : null}
        </div>
      ) : null}

      {/* HERO === remaining kcal. Hero font scales with viewport height so
          everything stays above the fold on shorter phones (Samsung S23
          Ultra ~770 CSS px). */}
      <section className="flex flex-col">
        <Eyebrow>{isToday ? t.heroRemainingToday : t.heroConsumedThatDay}</Eyebrow>
        <div className="flex items-start gap-1.5" style={{ marginTop: 4 }}>
          <span
            className="tabular"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 88,
              fontWeight: 800,
              lineHeight: 0.85,
              letterSpacing: "-0.06em",
              color: "var(--ink)",
            }}
          >
            {isToday ? kcalRemaining : kcalCurrent}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--accent)",
              fontWeight: 700,
              marginTop: 10,
            }}
          >
            kcal
          </span>
        </div>
        <p
          className="text-[14px] leading-snug max-w-[280px]"
          style={{ marginTop: 10, color: "var(--ink-2)" }}
        >
          {greetingName ? t.greetingHello(greetingName) : ""}
          {greetingTone(kcalRemaining, kcalTarget, mealsLogged, t)}
        </p>
      </section>

      {/* progress dashes (30 bars). No marginTop — the ScreenShell flex
          container already provides 16px gap between siblings; an extra
          margin here pushed content below the fold on S23 Ultra. */}
      <div className="flex gap-[3px]">
        {DASH_INDICES.map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: i % 3 === 0 ? 14 : 8,
              background: i < filledBars ? "var(--ink)" : "var(--track)",
              borderRadius: 2,
            }}
          />
        ))}
      </div>
      <div className="flex justify-between" style={{ marginTop: 6 }}>
        <MonoMeta>
          {kcalCurrent} / {kcalTarget || "?"} KCAL
        </MonoMeta>
        <MonoMeta>{pctDone}%</MonoMeta>
      </div>

      {/* macros table */}
      <section>
        <Eyebrow>{t.macrosLabel}</Eyebrow>
        <div style={{ marginTop: 8 }}>
          {macros.map((m) => {
            const pct = m.t > 0 ? Math.min(100, (m.v / m.t) * 100) : 0;
            return (
              <div
                key={m.l}
                className="flex items-center gap-3.5 py-2"
                style={{ borderBottom: "1px solid var(--nav-border)" }}
              >
                <div
                  className="flex-[0_0_70px] text-[13px] font-semibold"
                  style={{ color: "var(--ink)" }}
                >
                  {m.l}
                </div>
                <div className="flex-1 relative" style={{ height: 2, background: "var(--track)" }}>
                  <div
                    className="absolute left-0 top-0 bottom-0"
                    style={{ width: `${pct}%`, background: "var(--ink)" }}
                  />
                </div>
                <div
                  className="tabular text-right min-w-[60px]"
                  style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink)" }}
                >
                  <b>{Math.round(m.v)}</b>
                  <span style={{ color: "var(--muted)" }}>/{m.t}g</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* meals */}
      <section>
        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
          <Eyebrow>
            {isToday ? t.mealsToday : t.mealsThatDay} · {mealsLogged}{" "}
            {t.mealCount(mealsLogged)}
          </Eyebrow>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDay((d) => shiftDay(d, -1))}
              className="text-[12px] font-semibold transition-colors hover:opacity-70"
              style={{ color: "var(--ink-2)" }}
            >
              {t.prevDay}
            </button>
            {!isToday ? (
              <button
                type="button"
                onClick={() => setDay((d) => shiftDay(d, 1))}
                className="text-[12px] font-semibold transition-colors hover:opacity-70"
                style={{ color: "var(--ink-2)" }}
              >
                {t.nextDay}
              </button>
            ) : null}
          </div>
        </div>

        {loading ? (
          <p className="text-[13px] py-3" style={{ color: "var(--muted)" }}>
            {t.loading}
          </p>
        ) : error ? (
          <p className="text-[13px] py-3" style={{ color: "var(--bad-fg)" }}>
            {error}
          </p>
        ) : summary && summary.meals.length === 0 ? (
          <p className="text-[13px] py-6 text-center" style={{ color: "var(--muted)" }}>
            {isToday ? t.emptyToday : t.emptyThatDay}
          </p>
        ) : (
          <div>
            {summary?.meals.map((meal) => {
              const isFaved = favedMealIds.has(meal.id);
              const isFaving = favIngMealId === meal.id;
              const time = new Date(meal.loggedAt).toLocaleTimeString("pl-PL", {
                hour: "2-digit",
                minute: "2-digit",
              });
              const macroStr = [
                meal.proteinG !== null ? `b${Math.round(meal.proteinG)}` : null,
                meal.fatG !== null ? `t${Math.round(meal.fatG)}` : null,
                meal.carbsG !== null ? `w${Math.round(meal.carbsG)}` : null,
              ]
                .filter(Boolean)
                .join(" ");
              const expanded = expandedMealId === meal.id;
              const ingredients = ingredientsByMeal[meal.id];
              return (
                <div key={meal.id} style={{ borderTop: "1px solid var(--nav-border)" }}>
                  <div className="py-3.5 flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setEditingMeal(meal)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div
                      className="flex items-center gap-1.5 text-[14px] font-semibold"
                      style={{ color: "var(--ink)" }}
                    >
                      {isFaved ? (
                        <IconStar size={14} filled style={{ color: "var(--accent)" }} />
                      ) : null}
                      <span className="truncate">{meal.name}</span>
                    </div>
                    <div
                      className="text-[11px] mt-0.5"
                      style={{
                        fontFamily: "var(--font-mono)",
                        color: "var(--muted)",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {time}
                      {macroStr ? ` · ${macroStr}` : ""}
                    </div>
                  </button>
                  {meal.hasIngredients ? (
                    <button
                      type="button"
                      onClick={() => toggleExpand(meal)}
                      aria-expanded={expanded}
                      className="shrink-0"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: "transparent",
                        color: "var(--muted)",
                        boxShadow: "inset 0 0 0 1px var(--nav-border)",
                        border: "none",
                        fontSize: 11,
                        lineHeight: 1,
                      }}
                    >
                      {expanded ? "▴" : "▾"}
                    </button>
                  ) : null}
                  <div
                    className="tabular shrink-0"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 18,
                      fontWeight: 700,
                      color: "var(--ink)",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {meal.kcal}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0 ml-1">
                    <button
                      type="button"
                      onClick={() => saveAsFavorite(meal)}
                      disabled={isFaved || isFaving}
                      className="text-[10px] font-semibold transition-colors disabled:opacity-50"
                      style={{ color: "var(--accent)" }}
                      aria-label={t.addFavoriteAria}
                    >
                      <IconStar size={16} filled={isFaved} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDelete({ id: meal.id, name: meal.name })}
                      className="text-[10px] transition-colors"
                      style={{ color: "var(--muted)" }}
                      aria-label={t.deleteMealAria(meal.name)}
                    >
                      ×
                    </button>
                  </div>
                  </div>
                  {expanded && ingredients ? (
                    <div style={{ paddingBottom: 12, paddingLeft: 4 }}>
                      {ingredients.map((ing) => (
                        <div
                          key={ing.id}
                          className="flex items-center justify-between"
                          style={{ padding: "4px 0" }}
                        >
                          <span
                            className="truncate"
                            style={{ fontSize: 12, color: "var(--ink-2)" }}
                          >
                            ·{" "}
                            {ing.name}
                            {ing.portionGrams ? (
                              <span style={{ color: "var(--muted)" }}>
                                {" "}
                                {Math.round(ing.portionGrams)} g
                              </span>
                            ) : null}
                          </span>
                          <span
                            className="tabular shrink-0"
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                              color: "var(--muted)",
                            }}
                          >
                            {ing.kcal} kcal
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* favorites strip — adult-only. In child view the favorites list
          belongs to the adult and logging from there would post against
          the adult's dziennik, not the active child. */}
      {isToday && active.kind === "self" ? (
        <FavoritesStrip
          householdId={householdId}
          onLogged={(meal) => {
            setSummary((prev) => {
              if (!prev) return prev;
              const meals = [...prev.meals, meal];
              return { ...prev, meals, totals: totalsFromMeals(meals) };
            });
          }}
        />
      ) : null}

      {pendingDelete ? (
        <DeleteConfirmModal
          name={pendingDelete.name}
          submitting={deletingId === pendingDelete.id}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      ) : null}

      {editingMeal ? (
        <EditMealModal
          householdId={householdId}
          meal={editingMeal}
          onSaved={applyEdit}
          onCancel={() => setEditingMeal(null)}
        />
      ) : null}
    </ScreenShell>
  );
}

function DeleteConfirmModal({
  name,
  submitting,
  onCancel,
  onConfirm,
}: {
  name: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  // <dialog open> is non-modal (no top layer); body scrolls underneath on
  // iOS WKWebView without this lock (gotchas handoff §4.1).
  useModalScrollLock(true);
  return (
    <dialog
      open
      aria-modal="true"
      className="fixed inset-0 z-50 m-0 flex h-full w-full items-center justify-center bg-transparent p-4"
    >
      <button
        type="button"
        aria-label={t.closeAria}
        onClick={onCancel}
        className="absolute inset-0"
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />
      <div
        className="relative w-full max-w-sm p-5 flex flex-col gap-4"
        style={{
          background: "var(--surface)",
          borderRadius: 18,
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: "-0.03em",
            color: "var(--ink)",
          }}
        >
          {t.deleteTitle}
        </h2>
        <p className="text-[14px]" style={{ color: "var(--ink-2)" }}>
          {t.deleteBodyPrefix}
          <span className="font-semibold">{name}</span>
          {t.deleteBodySuffix}
        </p>
        <div className="flex flex-col gap-2">
          <PrimaryButton onClick={onConfirm} disabled={submitting}>
            {submitting ? t.deleting : t.deleteConfirm}
          </PrimaryButton>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="h-11 px-4 text-[13px] font-medium"
            style={{ color: "var(--ink-2)" }}
          >
            {t.deleteBack}
          </button>
        </div>
      </div>
    </dialog>
  );
}
